import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Sparkles, Loader2, Target, ExternalLink, Building2, Banknote, Award, Landmark, CheckCircle2, ArrowRight } from "lucide-react";
import { formationFinder, aiSave } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const LOCATIONS = ["United States", "Canada", "United Kingdom", "Europe", "Caribbean", "Australia", "Other"];
const BUILDING = ["AI Startup", "SaaS", "Agency", "E-commerce", "Mobile App", "Freelancer", "Other"];
const PRIORITY = ["Fast Setup", "Lowest Cost", "Legal Protection", "Not Sure"];

const ChipRow = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o)} className={`rounded-full px-3.5 py-2 text-xs font-semibold border transition-colors ${value === o ? "bg-[#0F0F0F] text-white border-[#0F0F0F]" : "bg-white text-[#374151] border-gray-200 hover:border-[#D4AF37]"}`} data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}>{o}</button>
    ))}
  </div>
);

export const FormationFinderModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user, requireAuth } = useAuth();
  const [location, setLocation] = useState("United States");
  const [building, setBuilding] = useState("");
  const [solo, setSolo] = useState("");
  const [priority, setPriority] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);

  if (!open) return null;

  const reset = () => { setResult(null); setBuilding(""); setSolo(""); setPriority(""); setSaved(false); };
  const close = () => { onClose(); setTimeout(reset, 200); };

  const generate = async () => {
    if (!building || !solo || !priority) { toast.info("Please answer all questions"); return; }
    setLoading(true);
    try {
      const d = await formationFinder({ location, building, solo, priority });
      setResult(d);
    } catch { toast.error("Could not generate a recommendation"); }
    finally { setLoading(false); }
  };

  const saveToNotebook = () => {
    const run = async () => {
      try { await aiSave("formation", { ...result, quiz: { location, building, solo, priority } }); setSaved(true); toast.success("Saved to your Startup Notebook"); }
      catch { toast.error("Could not save"); }
    };
    user ? run() : requireAuth(run);
  };

  const ALSO = result ? [
    { label: "Business Banking", icon: Banknote, ...(result.also?.banking || {}) },
    { label: "Trademark", icon: Award, ...(result.also?.trademark || {}) },
    { label: "EIN", icon: Landmark, ...(result.also?.ein || {}) },
  ].filter((x) => x.name) : [];

  return (
    <div className="fixed inset-0 z-[130] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="formation-finder-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-7 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute top-4 right-4 text-[#9aa1ab] hover:text-[#0F0F0F]" data-testid="ff-close"><X size={20} /></button>

        {loading ? (
          <div className="py-16 flex flex-col items-center text-center">
            <Loader2 size={34} className="animate-spin text-[#D4AF37]" />
            <p className="font-display font-bold text-[#0F0F0F] mt-4">LaunchBS AI is finding your best option…</p>
            <p className="text-sm text-[#687280] mt-1">Analyzing your answers</p>
          </div>
        ) : result ? (
          <div data-testid="ff-result">
            <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#B8901F]"><Target size={13} /> Recommended Business Structure</p>
            <h3 className="font-display text-2xl font-extrabold text-[#0F0F0F] mt-2" data-testid="ff-structure">{result.structure}</h3>
            <div className="mt-3 rounded-2xl bg-[#FBF7EA]/60 border border-[#D4AF37]/20 p-4">
              <p className="text-xs font-bold text-[#8a6d1a] mb-1">Why?</p>
              <p className="text-sm text-[#374151]">{result.why}</p>
            </div>

            {result.platform?.name && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-[#687280] mb-2">Recommended Platform</p>
                <a href={result.platform.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-2xl border border-gray-100 p-4 hover:border-[#D4AF37] transition-colors" data-testid="ff-platform">
                  <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 text-[#B8901F] flex items-center justify-center"><Building2 size={18} /></span>
                  <span className="font-display font-bold text-[#0F0F0F] flex-1">{result.platform.name}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-white bg-[#0F0F0F] rounded-full px-3 py-1.5">Visit <ExternalLink size={11} /></span>
                </a>
              </div>
            )}

            {ALSO.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-[#687280] mb-2">Also Recommended</p>
                <div className="space-y-2">
                  {ALSO.map((a) => (
                    <a key={a.label} href={a.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-xl border border-gray-100 px-3.5 py-2.5 hover:border-[#D4AF37] transition-colors">
                      <a.icon size={15} className="text-[#B8901F]" />
                      <span className="text-sm text-[#374151] flex-1"><span className="font-semibold text-[#0F0F0F]">{a.label}</span> → {a.name}</span>
                      <ExternalLink size={13} className="text-[#9aa1ab]" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {(result.next_steps || []).length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-[#687280] mb-2">Next Steps</p>
                <div className="space-y-1.5">
                  {result.next_steps.map((s, i) => (
                    <p key={i} className="flex items-center gap-2 text-sm text-[#374151]"><CheckCircle2 size={16} className="text-green-500" /> {s}</p>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={() => { close(); navigate("/resources/business-formation"); }} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full py-3 text-sm font-bold text-[#374151] border border-gray-200 hover:border-[#D4AF37]" data-testid="ff-view-resources">View Resources</button>
              <button onClick={saveToNotebook} disabled={saved} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#0F0F0F] text-white rounded-full py-3 text-sm font-bold hover:bg-[#1c1c1c] disabled:opacity-60" data-testid="ff-save">{saved ? "Saved ✓" : "Save to Notebook"}</button>
            </div>
          </div>
        ) : (
          <div data-testid="ff-quiz">
            <h3 className="font-display text-xl font-extrabold text-[#0F0F0F]">Find Your Best Business Formation</h3>
            <p className="text-sm text-[#687280] mt-1">Answer a few quick questions and LaunchBS AI will recommend the best setup.</p>

            <div className="mt-5 space-y-5">
              <div>
                <p className="text-sm font-bold text-[#0F0F0F] mb-2">Where are you located?</p>
                <select value={location} onChange={(e) => setLocation(e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="ff-location">
                  {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <p className="text-sm font-bold text-[#0F0F0F] mb-2">What are you building?</p>
                <ChipRow options={BUILDING} value={building} onChange={setBuilding} testid="ff-building" />
              </div>
              <div>
                <p className="text-sm font-bold text-[#0F0F0F] mb-2">Are you starting alone?</p>
                <ChipRow options={["Yes", "No"]} value={solo} onChange={setSolo} testid="ff-solo" />
              </div>
              <div>
                <p className="text-sm font-bold text-[#0F0F0F] mb-2">What's most important?</p>
                <ChipRow options={PRIORITY} value={priority} onChange={setPriority} testid="ff-priority" />
              </div>
            </div>

            <button onClick={generate} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="ff-generate">
              <Sparkles size={16} /> Generate Recommendation <ArrowRight size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
